/**
 * Shared factory used by every primitive in `/core`.
 *
 * - `usePolymorphic` resolves the `as` prop into a render element.
 * - `useBaseAttrs` rolls up the `BaseProps` baseline (className, style,
 *   data-testid, surface CSS vars, animation attrs, a11y forwards) into a
 *   single spread object so each component file stays terse.
 */

import { useMemo } from 'react';
import type { ElementType, CSSProperties } from 'react';
import { classes, type ClassValue } from '@repletoryreach/sdk-utils';
import {
  buildAnimationAttrs,
  resolveSurfaceStyle,
  resolveRadius,
  resolveShadow,
  type BaseProps, resolveInstanceColors } from '../../theme/index.js';

export interface BaseAttrs {
  className: string;
  style: CSSProperties;
  attrs: Record<string, string | number | boolean | undefined>;
}

/**
 * Combine block name, modifiers, animation attrs, surface CSS vars and the
 * caller-supplied className/style into a single spread payload.
 */
export function useBaseAttrs(
  blockClass: string,
  props: BaseProps,
  modifiers: Array<ClassValue> = []
): BaseAttrs {
  return useMemo(() => {
    const anim = buildAnimationAttrs(props);
    const surface = resolveSurfaceStyle(props.surface);
    const radius = resolveRadius(props.radius);
    const shadow = resolveShadow(props.shadow);

    const className = classes(
      blockClass,
      ...modifiers,
      props.unstyled && `${blockClass}--unstyled`,
      props.loading && `${blockClass}--loading`,
      props.disabled && `${blockClass}--disabled`,
      props.invalid && `${blockClass}--invalid`,
      props.active && `${blockClass}--active`,
      props.selected && `${blockClass}--selected`,
      props.className
    );

    const instance = resolveInstanceColors(props.color);

    const style: CSSProperties = {
      ...surface,
      ...(instance as CSSProperties | undefined),
      ...(radius ? { borderRadius: radius } : null),
      ...(shadow ? { boxShadow: shadow } : null),
      ...anim.style,
      ...props.style
    };

    const attrs: Record<string, string | number | boolean | undefined> = {
      ...anim.attrs,
      'data-testid': props['data-testid'],
      'data-rr-color': props.color,
      'data-rr-variant': props.variant,
      'data-rr-size': typeof props.size === 'number' ? `${props.size}px` : (props.size as string | undefined),
      'data-rr-density': props.density,
      'data-rr-text-scale': props.textScale,
      'data-rr-loading': props.loading || undefined,
      'data-rr-disabled': props.disabled || undefined,
      'data-rr-invalid': props.invalid || undefined,
      'data-rr-active': props.active || undefined,
      'data-rr-selected': props.selected || undefined
    };

    return { className, style, attrs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockClass, props, ...modifiers]);
}

export function resolveAs<E extends ElementType>(
  as: E | undefined,
  fallback: ElementType
): ElementType {
  return (as ?? fallback) as ElementType;
}

/**
 * Every `BaseProps` key `useBaseAttrs` consumes and turns into a class, a CSS
 * variable or a `data-` attribute.
 *
 * These must NOT reach the DOM: React would render `surface="card"` or
 * `entrance="fadeUp"` as literal attributes, and `loading`/`active`/`selected`
 * would draw "Received `true` for a non-boolean attribute" warnings.
 *
 * Deliberately absent, so they DO reach the DOM: `id`, `role`, `tabIndex` and
 * the three `aria-*` forwards. They are real HTML attributes that happen to be
 * declared on `BaseProps`, and dropping them was silently costing every
 * component its accessible name.
 */
const CONSUMED_BASE_PROPS = [
  'className', 'style', 'unstyled', 'data-testid',
  'size', 'radius', 'shadow', 'color', 'variant', 'gradient', 'surface', 'density', 'textScale',
  'loading', 'invalid', 'active', 'selected',
  'entrance', 'entranceDelay', 'entranceDuration', 'entranceEasing', 'entranceTrigger', 'entranceOnce',
  'hover', 'focusAnim', 'loop', 'exit', 'staggerIndex', 'reducedMotion',
  'onMount', 'onUnmount',
  // Never spread from props: every component renders its children
  // explicitly, and one component passes a render function as `children`.
  'children'
] as const;

/**
 * Split a component's props into the part `useBaseAttrs` renders and the part
 * that belongs on the DOM element.
 *
 * Without this, a component that spread only `base.attrs` dropped every prop
 * outside the curated allowlist: `onClick`, `onKeyDown`, `href`, `title`,
 * `name`, `form`, every `data-*`. That is why the ✕ on Drawer and Modal did
 * nothing while the Cancel button beside it worked. `Button` had already been
 * patched by hand with a 30-line destructure; this is that fix, once, for
 * everyone.
 *
 * `disabled`, `readOnly` and `required` stay in the DOM half on purpose: they
 * are valid on form controls, and the components that render a `<div>` rather
 * than a control destructure them before they ever get here.
 */
export function splitBaseProps<P extends Record<string, unknown>>(
  props: P,
  /** Extra keys this component consumes itself, e.g. `label`, `leftSection`. */
  alsoConsumed: readonly string[] = []
): { base: BaseProps; dom: Record<string, unknown> } {
  const dom: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if ((CONSUMED_BASE_PROPS as readonly string[]).includes(key)) continue;
    if (alsoConsumed.includes(key)) continue;
    dom[key] = props[key];
  }
  return { base: props as BaseProps, dom };
}
